<template>
	<div class="login-page-container">
		<div
			style="
				position: fixed;
				top: 20px;
				z-index: 49;
				backdrop-filter: blur(2px);
				display: flex;
				justify-content: center;
			"
			v-if="LoaderStatus"
		>
			<Spinner />
		</div>
		<div
			class="mobileLoginPage-container d-flex align-items-center justify-content-center"
		></div>
		<div class="d-flex flex-column w-100 dynamic-parent">
			<!-- Header with Mycareer title -->
			<div class="login-header">
				<img
            		src="https://cvstaticfiles.s3.ap-south-1.amazonaws.com/Hire3xAssests/Logo/SVG/Hire3xwhitebeta.svg"
            		alt=""
          		/>
			</div>

			<!-- Main Registration Card -->
			<div class="login-card">
				<!-- Title Section -->
				<div class="welcome-section">
					<h2 class="welcome-title">Register!</h2>
					<p class="welcome-text">
						Please fill in below information to create your account with us.
					</p>
				</div>

				<!-- Error Message -->
				<!-- <div id="message" class="error-message">jj</div> -->

				<!-- First Name Input -->
				<div class="input-group">
					<div class="input-wrapper">
						<label
							class="input-label"
							for="input-firstname"
							:class="{ active: firstName || isFirstNameFocused }"
							>First Name</label
						>
						<b-form-input
							class="form-input"
							id="input-firstname"
							type="text"
							ref="fname"
							@keypress="isLetter"
							v-model.trim="$v.firstName.$model"
							:class="{ error: $v.firstName.$error }"
							@focus="isFirstNameFocused = true"
							@blur="isFirstNameFocused = false"
						></b-form-input>
					</div>
					<div
						class="validation-message"
						v-if="$v.firstName.$dirty && !$v.firstName.required"
					>
						Required
					</div>
					<div
						class="validation-message"
						v-else-if="!$v.firstName.alphaNumeric && $v.firstName.required"
					>
						Invalid First Name
					</div>
					<div
						class="validation-message"
						v-else-if="!$v.firstName.maxLength && $v.firstName.required"
					>
						Should not exceed 38 characters
					</div>
				</div>

				<!-- Last Name Input -->
				<div class="input-group">
					<div class="input-wrapper">
						<label
							class="input-label"
							for="input-lastname"
							:class="{ active: lastName || isLastNameFocused }"
							>Last Name</label
						>
						<b-form-input
							class="form-input"
							id="input-lastname"
							type="text"
							ref="lname"
							@keypress="isLetterandSpace"
							v-model.trim="$v.lastName.$model"
							:class="{ error: $v.lastName.$error }"
							@focus="isLastNameFocused = true"
							@blur="isLastNameFocused = false"
						></b-form-input>
					</div>
					<div
						class="validation-message"
						v-if="$v.lastName.$dirty && !$v.lastName.required"
					>
						Required
					</div>
					<div
						class="validation-message"
						v-else-if="!$v.lastName.alphaNumeric && $v.lastName.required"
					>
						Invalid Last Name
					</div>
					<div
						class="validation-message"
						v-else-if="!$v.lastName.maxLength && $v.lastName.required"
					>
						Should not exceed 38 characters
					</div>
				</div>

				<!-- Email Input -->
				<div class="input-group">
					<div class="input-wrapper">
						<label
							class="input-label"
							for="input-email"
							:class="{ active: email || isEmailFocused }"
							>Email</label
						>
						<b-form-input
							class="form-input"
							id="input-email"
							type="text"
							ref="mail"
							v-model.trim="$v.email.$model"
							@keydown.space.prevent
							:class="{ error: $v.email.$error || emailExists }"
							@focus="isEmailFocused = true"
							@blur="isEmailFocused = false"
						></b-form-input>
					</div>
					<div
						class="validation-message"
						v-if="$v.email.$dirty && !$v.email.required"
					>
						Required
					</div>
					<div
						class="validation-message"
						v-else-if="
							$v.email.required && (!$v.email.email || !$v.email.alphaEmail)
						"
					>
						Invalid Email
					</div>
					<div class="validation-message" v-else-if="!$v.email.maxLength">
						Should not exceed 64 characters
					</div>
					<div class="validation-message" v-else-if="emailExists">
						Email already exists
					</div>
				</div>

				<!-- Phone Input with Country Code -->
				<div class="input-group phone-input-group">
					<div class="phone-input-wrapper-intl">
						<label
							class="input-label phone"
							for="candidatePhone"
							:class="{ active: isFocused || phoneNumber }"
							>Phone</label
						>
						<b-form-input
							ref="phoneInput"
							id="candidatePhone"
							type="tel"
							v-model.trim="$v.phoneNumber.$model"
							autocomplete="off"
							@keypress="numbersOnly"
							@focus="handleFocus"
							@blur="handleBlur"
							:class="{
								error: $v.phoneNumber.$error || invalidPhone,
							}"
							class="form-input phone-input-intl"
						></b-form-input>
					</div>
					<div
						class="validation-message"
						v-if="$v.phoneNumber.$dirty && !$v.phoneNumber.required"
					>
						Required
					</div>
					<div
						class="validation-message"
						v-if="invalidPhone && $v.phoneNumber.required"
					>
						Invalid Phone Number
					</div>
				</div>

				<!-- Password Input -->
				<div class="input-group password-group">
					<div class="input-wrapper">
						<label
							class="input-label"
							for="input-password"
							id="password-label"
							:class="{ active: password || isPasswordFocused }"
							>Password</label
						>
						<b-form-input
							class="form-input"
							id="input-password"
							type="text"
							v-if="showPassword"
							v-model.trim="$v.password.$model"
							@input="validatePassword"
							ref="pass"
							:class="{ error: $v.password.$error || validPassword }"
							@focus="isPasswordFocused = true"
							@blur="isPasswordFocused = false"
						>
						</b-form-input>
						<b-form-input
							class="form-input"
							type="password"
							v-else
							v-model.trim="$v.password.$model"
							@input="validatePassword"
							ref="pass"
							:class="{ error: $v.password.$error || validPassword }"
							aria-labelledby="password-label"
							@focus="isPasswordFocused = true"
							@blur="isPasswordFocused = false"
						>
						</b-form-input>
						<div class="password-toggle pointer" @click="toggleShow">
							<img
								v-if="showPassword"
								src="../../assets/New Profile/view-icon.svg"
								alt="Hide password"
							/>
							<img
								v-else
								src="../../assets/New Profile/not-view-icon.svg"
								alt="Show password"
							/>
						</div>
					</div>
					<div
						class="validation-message"
						v-if="$v.password.$dirty && !$v.password.required"
					>
						Required
					</div>
					<div class="validation-message" v-else-if="validPassword">
						{{ passwordValidation }}
					</div>
					<div
						class="validation-message"
						v-else-if="!$v.password.maxLength && $v.password.required"
					>
						Should not exceed 55 characters
					</div>
				</div>

				<!-- Confirm Password Input -->
				<div class="input-group password-group">
					<div class="input-wrapper">
						<label
							class="input-label"
							for="input-confirm-password"
							id="confirm-password-label"
							:class="{ active: confirmPassword || isConfirmPasswordFocused }"
							>Confirm Password</label
						>
						<b-form-input
							class="form-input"
							id="input-confirm-password"
							type="password"
							ref="cpass"
							v-model.trim="$v.confirmPassword.$model"
							@paste="preventPaste"
							:class="{ error: $v.confirmPassword.$error || match }"
							@focus="isConfirmPasswordFocused = true"
							@blur="isConfirmPasswordFocused = false"
						>
						</b-form-input>
					</div>
					<div
						class="validation-message"
						v-if="$v.confirmPassword.$dirty && !$v.confirmPassword.required"
					>
						Required
					</div>
					<div
						class="validation-message"
						v-else-if="
							!$v.confirmPassword.maxLength && $v.confirmPassword.required
						"
					>
						Should not exceed 55 characters
					</div>
					<div class="validation-message" v-else-if="match">
						Password mismatch
					</div>
				</div>

				<!-- Promo/Referral Code Input -->
				<div class="input-group">
					<div class="input-wrapper">
						<label
							class="input-label"
							for="input-promo"
							:class="{ active: referralCode || isReferralCodeFocused }"
							>Promo/Referral Code (if any)</label
						>
						<b-form-input
							class="form-input"
							id="input-promo"
							type="text"
							ref="referal"
							v-model.trim="$v.referralCode.$model"
							@keydown.space.prevent
							@input="convertToUppercase"
							maxlength="8"
							:class="{ error: wrongReferral }"
							@focus="isReferralCodeFocused = true"
							@blur="isReferralCodeFocused = false"
						></b-form-input>
					</div>
					<div class="validation-message" v-if="wrongReferral">
						Invalid referral code
					</div>
				</div>

				<!-- Terms and Conditions Checkbox -->
				<div class="terms-section">
					<div class="terms-checkbox-wrapper">
						<b-form-checkbox
							id="checkbox-1"
							v-model="termsCondition"
							class="terms-checkbox-input"
						>
						</b-form-checkbox>
						<div class="terms-text">
							I agree to the&nbsp;
							<span @click="terms" class="terms-link pointer">
								<u>Terms and Conditions&nbsp;</u>
							</span>
							&nbsp;
							<span class="terms-link pointer" @click="goToPrivacyPolicy">
								<u>Privacy Policy</u>
							</span>
							of Hire3x.com
						</div>
					</div>
				</div>

				<!-- Register Button -->
				<button
					@click="termsCondition ? handleRegister() : ''"
					class="login-button mt-4"
					:class="{ 'opac-5': !termsCondition }"
				>
					Register
					<div>
						<img src="../../assets/login/ArrowIcon.svg" alt="Arrow Icon" />
					</div>
				</button>

				<!-- Login Link -->

				<div class="register-section">
					<div class="register-text">Already have account?</div>
					<router-link to="/login" class="login-link"> Login here </router-link>
				</div>
			</div>

			<div class="login-footer">
				Copyrights © 2024 Hire3x - All rights reserved.
			</div>
		</div>
	</div>
</template>

<script>
import {
	required,
	email,
	sameAs,
	maxLength,
	helpers,
} from "vuelidate/lib/validators";
import { sendCandOtp, checkCandidateDirect } from "../../utils/apiFunctions";
import errorModal from "../../components/mixin/toast";
import Spinner from "../../components/fixedcomponents/Spinner.vue";
import { debounce } from "lodash";
import "intl-tel-input/build/css/intlTelInput.css";
import intlTelInput from "intl-tel-input";

export default {
	name: "RegisterPage",
	components: {
		Spinner,
	},
	mixins: [errorModal],
	data() {
		return {
			firstName: "",
			lastName: "",
			email: "",
			phoneNumber: "",
			password: "",
			confirmPassword: "",
			referralCode: "",
			showPassword: false,
			showConfirmPassword: false,
			LoaderStatus: false,
			countryCode: "+91",
			isFocused: false,
			invalidPhone: false,
			emailExists: false,
			wrongReferral: false,
			termsCondition: false,
			validPassword: false,
			match: false,
			passwordminlength: "",
			passwordnumber: "",
			passwordspecial: "",
			passwordupper: "",
			passwordlower: "",
			isFirstNameFocused: false,
			isLastNameFocused: false,
			isEmailFocused: false,
			isPasswordFocused: false,
			isConfirmPasswordFocused: false,
			isReferralCodeFocused: false,
		};
	},
	validations() {
		return {
			firstName: {
				required,
				maxLength: maxLength(38),
				alphaNumeric: helpers.regex(
					"alphaNumeric",
					/^(?![ .])[a-zA-Z\s.]+(?:\.[a-zA-Z\s.]+)?$/
				),
			},
			lastName: {
				required,
				maxLength: maxLength(38),
				alphaNumeric: helpers.regex(
					"alphaNumeric",
					/^(?![ .])[a-zA-Z]+(?:\.[a-zA-Z]+)?$/
				),
			},
			email: {
				required,
				email,
				alphaEmail: (value) => {
					if (value === null) return true;
					return /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/.test(value);
				},
				maxLength: maxLength(64),
			},
			phoneNumber: {
				required,
			},
			password: {
				required,
				maxLength: maxLength(55),
			},
			confirmPassword: {
				required,
				maxLength: maxLength(55),
			},
			referralCode: {},
		};
	},
	computed: {
		passwordValidation() {
			let val = [];
			if (this.passwordminlength) {
				val.push(this.passwordminlength);
			}
			if (this.passwordupper) {
				val.push(this.passwordupper);
			}
			if (this.passwordlower) {
				val.push(this.passwordlower);
			}
			if (this.passwordspecial) {
				val.push(this.passwordspecial);
			}
			if (this.passwordnumber) {
				val.push(this.passwordnumber);
			}
			return val.join(", ");
		},
		checkpassowrdInvalid() {
			return (
				this.passwordminlength ||
				this.passwordupper ||
				this.passwordlower ||
				this.passwordspecial ||
				this.passwordnumber
			);
		},
	},
	watch: {
		phoneNumber(val) {
			const value = val.replace(/\s+/g, "");
			if (this.$v.phoneNumber.$dirty && this.countryCode == "+91") {
				const indianPhoneNumberRegex = /^[6-9]\d{9}$/;
				if (indianPhoneNumberRegex.test(value)) {
					this.invalidPhone = false;
				} else {
					this.invalidPhone = true;
				}
			}
		},
		password(value) {
			if (this.confirmPassword) {
				if (value == this.confirmPassword) {
					this.match = false;
				} else {
					this.match = true;
				}
			}
		},
		confirmPassword(value) {
			if (value == this.password) {
				this.match = false;
			} else {
				this.match = true;
			}
		},
		email(value) {
			if (value) {
				this.email = value.replace(/\s/, "").toLowerCase();
				if (!this.$v.email.$error && !this.$v.email.$invalid) {
					this.debouncedCheckEmail();
				} else {
					this.emailExists = false;
				}
			} else {
				this.emailExists = false;
			}
		},
		referralCode(value) {
			if (value != "" || value != null) {
				this.wrongReferral = false;
			}
		},
	},
	methods: {
		toggleShow() {
			this.showPassword = !this.showPassword;
		},
		handleFocus() {
			this.isFocused = true;
		},
		handleBlur() {
			this.isFocused = false;
		},
		numbersOnly(evt) {
			if (evt.target.nodeName === "INPUT") {
				const charCode = evt.key;
				if ((charCode < "0" || charCode > "9") && charCode !== ".") {
					evt.preventDefault();
				} else {
					if (this.countryCode == "+91" && this.phoneNumber.length >= 11) {
						evt.preventDefault();
					}
				}
			}
		},
		isLetter(e) {
			let char = String.fromCharCode(e.keyCode);
			if (/^[A-Za-z .]+$/.test(char)) return true;
			else e.preventDefault();
		},
		isLetterandSpace(e) {
			let char = String.fromCharCode(e.keyCode);
			if (/^[A-Za-z]+$/.test(char)) return true;
			else e.preventDefault();
		},
		preventPaste(event) {
			event.preventDefault();
		},
		validatePassword() {
			if (this.password.length < 6) {
				this.passwordminlength = "Min 6 char required";
			} else {
				this.passwordminlength = "";
			}
			if (!/^(.*[A-Z]){1,}.*$/.test(this.password)) {
				this.passwordupper = "1 Upper Case";
			} else {
				this.passwordupper = "";
			}
			if (!/^(.*[a-z]){3}.*$/.test(this.password)) {
				this.passwordlower = "3 Lower Case";
			} else {
				this.passwordlower = "";
			}
			if (!/^(.*[!@#$%^&*()_+{}\[\]:;<>,.?~\\/-]){1,}.*$/.test(this.password)) {
				this.passwordspecial = "1 special character";
			} else {
				this.passwordspecial = "";
			}
			if (!/^(.*\d){1,}.*$/.test(this.password)) {
				this.passwordnumber = "1 number";
			} else {
				this.passwordnumber = "";
			}
			if (
				this.passwordminlength ||
				this.passwordupper ||
				this.passwordlower ||
				this.passwordspecial ||
				this.passwordnumber
			) {
				this.validPassword = true;
			} else {
				this.validPassword = false;
			}
		},
		convertToUppercase() {
			this.referralCode = this.referralCode.toUpperCase();
		},
		terms() {
			const link = document.createElement("a");
			link.href =
				"https://cvstaticfiles.s3.ap-south-1.amazonaws.com/candidate/documents/Terms+and+Conditions+Hire3x.docx";
			link.download = "Terms_and_Conditions_Hire3x.docx";
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		},
		goToPrivacyPolicy() {
			window.open(
				"https://cvstaticfiles.s3.ap-south-1.amazonaws.com/candidate/documents/General+Privacy+Policy+for+hire3x.pdf"
			);
		},
		debouncedCheckEmail: debounce(function () {
			this.checkCandidateDirect();
		}, 500),
		async checkCandidateDirect() {
			try {
				const res = await checkCandidateDirect({ email: this.email });
				if (res.status == 200) {
					this.emailExists = res.data.data?.isCandidateValid || false;
				} else {
					this.emailExists = false;
				}
			} catch (error) {
				console.error(error);
				this.emailExists = false;
			}
		},
		async handleRegister() {
			this.$refs.pass?.blur();
			this.$refs.fname?.blur();
			this.$refs.lname?.blur();
			this.$refs.mail?.blur();
			this.$refs.phoneInput?.blur();
			this.$refs.referal?.blur();

			if (this.$v.firstName.$invalid) {
				this.$v.firstName.$touch();
			} else if (this.$v.lastName.$invalid) {
				this.$v.lastName.$touch();
			} else if (this.$v.email.$invalid) {
				this.$v.email.$touch();
			} else if (this.$v.phoneNumber.$invalid || this.invalidPhone) {
				this.$v.phoneNumber.$touch();
			} else if (this.$v.password.$invalid || this.validPassword) {
				this.$v.password.$touch();
			} else if (this.$v.confirmPassword.$invalid || this.match) {
				this.$v.confirmPassword.$touch();
			}

			if (this.termsCondition) {
				if (
					!this.$v.$invalid &&
					!this.match &&
					!this.validPassword &&
					!this.invalidPhone &&
					!this.emailExists
				) {
					this.LoaderStatus = true;
					try {
						let userdata = {
							email: this.email.trim(),
							firstName: this.firstName.trim(),
							lastName: this.lastName.trim(),
							password: this.password.trim(),
							countryCode: this.countryCode.trim(),
							phoneNumber: this.phoneNumber.replace(/\s+/g, ""),
						};
						if (this.referralCode != "" && this.referralCode != null) {
							userdata["code"] = this.referralCode.trim();
						}
						this.$store.commit("userVUEX/setUserData", userdata);

						let payload = {
							type: "register-account",
							email: this.email.trim(),
							firstName: this.firstName.trim(),
							lastName: this.lastName.trim(),
						};
						if (this.referralCode != "" && this.referralCode != null) {
							payload["code"] = this.referralCode.trim();
						}

						const response = await sendCandOtp(payload);
						if (response.status == 200) {
							this.LoaderStatus = false;
							// Redirect to OTP verification page
							this.$router.push({
								path: "/verify-otp",
								query: {
									email: this.email,
									type: "register",
								},
							});
						} else if (response.status == 204) {
							this.LoaderStatus = false;
							this.wrongReferral = true;
						} else {
							this.LoaderStatus = false;
							this.errorModal("Error", "Registration failed!");
						}
					} catch (error) {
						this.LoaderStatus = false;
						if (error.response) {
							if (error.response.status == 409) {
								this.emailExists = true;
								this.errorModal("Error", "Email already exists. Please login.");
							} else if (error.response.status == 204) {
								this.wrongReferral = true;
							} else {
								this.errorModal(
									"Error",
									error.response.data?.data?.Detail ||
										"Registration failed. Please try again."
								);
							}
						} else {
							this.errorModal(
								"Error",
								"Registration failed. Please try again."
							);
						}
					}
				}
			}
		},
		initializeTel() {
			this.$nextTick(() => {
				const input = document.querySelector("#candidatePhone");
				if (input) {
					const iti = intlTelInput(input, {
						utilsScript:
							"https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/19.5.3/js/utils.js",
						showSelectedDialCode: true,
						initialCountry: "IN",
					});

					input.addEventListener("countrychange", () => {
						const code = iti.getSelectedCountryData().dialCode;
						const value = this.phoneNumber.replace(/\s+/g, "");
						this.countryCode = "+" + code;
						if (this.countryCode != "+91" && this.phoneNumber) {
							this.invalidPhone = !iti.isValidNumber();
						} else if (this.countryCode == "+91") {
							const indianPhoneNumberRegex = /^[6-9]\d{9}$/;
							if (indianPhoneNumberRegex.test(value)) {
								this.invalidPhone = false;
							} else {
								this.invalidPhone = true;
							}
						}
					});

					input.addEventListener("input", () => {
						if (this.countryCode != "+91") {
							this.invalidPhone = !iti.isValidNumber();
						}
					});
				}
			});
		},
	},
	created() {
		this.$store.commit("userVUEX/setUserData", {});
		if (this.$route.query.marketingmail && this.$route.query.uuid) {
			// Handle marketing mail if needed
		}
	},
	mounted() {
		this.initializeTel();
	},
};
</script>

<style scoped>
.login-page-container {
	height: 100dvh;
	background: #f4f4f4;
	padding: 10px;
	position: relative;
	display: flex;
	justify-content: center;
}

/* .login-page-container::before {
	content: "";
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	height: 50%;
	background: linear-gradient(158.58deg, #20639b -1.63%, #09b7c2 105.98%);
	z-index: 0;
} */

/* Header Section */
.login-header {
	width: 100%;
	text-align: center;
	margin-top: 13%;
	position: relative;
	z-index: 1;
}

.app-title {
	color: #ffffff;
	font-size: 25px;
	font-weight: 500;
	margin: 0;
	font-family: "Poppins", sans-serif;
}

/* Login Card */
.login-card {
	margin-top: 24px;
	z-index: 5;
	width: 98%;
	padding: 15px;
	border-radius: 12px;
	background: var(--White, #fff);
	box-shadow: 0 8px 50px -2px rgba(16, 24, 40, 0.08);
	height: -moz-fit-content;
	height: fit-content;
	max-height: fit-content;
}

/* Welcome Section */

.welcome-title {
	font-size: 30px;
	font-weight: 600;
	font-family: "lato", sans-serif;
	color: #464646;
	text-align: center;
}

.welcome-text {
	font-size: 15px;
	font-weight: 500;
	color: #3a3c40;
	font-family: "montserrat", sans-serif;
	text-align: center;
	margin: 0;
}

/* Error Message */
.error-message {
	color: #dc3545;
	font-size: 14px;
	margin-bottom: 15px;
	min-height: 20px;
}

/* Input Groups */
.input-group {
	position: relative;
	margin-top: 15px;
}

.input-wrapper {
	position: relative;
	width: 100%;
}

.input-label {
	position: absolute;
	left: 12px;
	top: 50%;
	transform: translateY(-50%);
	pointer-events: none;
	transition: all 0.2s ease;
	background: #ffffff;
	padding: 0 4px;
	z-index: 1;
	opacity: 0.5;
}
.input-label.phone {
	left: 82px;
	width: 100px;
	opacity: 1;
	color: #909294;
}

.input-label.phone.active {
	left: 12px;
	width: fit-content;
}

.input-label.active {
	opacity: 1;
	z-index: 2;
	top: 0;
	transform: translateY(-50%);
	font-size: 16px;
	color: #667085;
	background: #ffffff;
}

.form-input {
	color: #333;
	font-family: Inter, sans-serif;
	font-size: 16px;
	font-weight: 400;
	line-height: 19.36px;
	height: 45px;
	padding: 14px 20px;
	border-radius: 8px;
	border: 1px solid var(--gray-300, #d0d5dd);
	background: var(--White, #fff);
	opacity: 1;
}

.form-input:focus {
	outline: none;
	border: 1px solid var(--gray-300, #d0d5dd);
	box-shadow: 0 0 0 3px rgba(45, 90, 138, 0.1);
}

.form-input.error {
	border-color: #dc3545;
}

/* Validation Messages */
.validation-message {
	color: #dc143c;
	font-family: "Inter", sans-serif;
	font-size: 12px;
	font-style: normal;
	font-weight: 400;
}

/* Password Toggle */
.password-toggle {
	position: absolute;
	top: 50%;
	right: 12px;
	transform: translateY(-50%);
	width: 20px;
	height: 20px;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
}

.password-toggle img {
	width: 18px;
	height: 18px;
}

.pointer {
	cursor: pointer;
}

/* Forgot Password */
.forgot-password-wrapper {
	font-weight: 500;
	color: #20639b;
	font-family: "Inter", sans-serif;
	font-size: 15px;
}

.forgot-password-link {
	font-weight: 500;
	color: #20639b;
	font-family: "Inter", sans-serif;
	font-size: 15px;
}

.forgot-password-link:hover {
	text-decoration: underline;
}
.mobileLoginPage-container {
	position: absolute;
	top: 0;
	z-index: 5;
	height: 50%;
	width: 100%;
	background: linear-gradient(158.58deg, #20639b -1.63%, #09b7c2 105.98%);
}
.dynamic-parent {
	overflow: hidden auto;
	background: transparent;
	z-index: 22;
	margin-right: -10px;
	padding-bottom: env(safe-area-inset-bottom);
	padding-top: env(safe-area-inset-top);
}
/* Login Button */
.login-button {
	width: 100%;
	padding: 12px;
	background-color: #2d5a8a;
	color: #ffffff;
	border: none;
	border-radius: 6px;
	font-size: 16px;
	font-weight: 600;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	transition: background-color 0.3s;
}

.login-button:hover {
	background-color: #1e3a5f;
}

.arrow-icon {
	font-size: 18px;
}

/* Footer */
.login-footer {
	text-align: center;
	font-size: 12px;
	color: #999999;
	padding-top: 10px;
}
.terms-checkbox-wrapper {
	display: flex;
	align-items: center;
	margin-top: 16px;
	font-size: 12px;
	color: #667085;
	font-weight: 400;
	font-family: "Lato", sans-serif;
}
.terms-text u {
	color: #20639b;
	font-size: 12px;
	font-weight: 400;
	font-family: "Lato", sans-serif;
}
/* Register Section */
.register-section {
	text-align: center;
	margin-bottom: 5px;
	margin-top: 15px;
}
.register-section a {
	color: #20639b;
}
.register-text {
	color: #667085;
	font-family: "Inter", sans-serif;
	font-size: 14px;
	font-style: normal;
	font-weight: 400;
}

.register-link {
	text-decoration: none;

	font-family: "Inter", sans-serif;
	font-weight: 500;
	font-size: 16px;
	color: #20639b;
}
</style>
<style>
.iti--show-selected-dial-code .iti__selected-flag {
	background-color: #fff;
	border-radius: 10px;
}
.iti__flag-container {
	font-family: "poppins", sans-serif;
}
.iti input::placeholder {
	color: #fff;
}
.terms-checkbox-wrapper
	.custom-control-input:checked
	~ .custom-control-label::before {
	color: #fff;
	border-color: #20639b;
	background-color: #20639b;
}

.phone-input-group {
    overflow: visible;
}
</style>
